"use client";
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FaStar } from "react-icons/fa";
import { FiEdit2, FiChevronDown, FiCheck } from "react-icons/fi";
import { Tag, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { REST_API_URL } from "../../../api/config";
import { buildHeaders } from "../../../api/buildHeaders";
import {getCurrencySymbol} from "../../../utils/currency/getCurrencySymbol"
import AddClientModal from "../Modals/ClientModal";
import { useAppSelector } from "../../../hooks/redux/useRedux";
import { selectUserCurrency } from "../../../redux/slices/profileSlice/ProfileSlice";
import { PrimaryButton } from "../../../components/buttons/primaryButton/PrimaryButton.tsx";
import { SelectButton } from "../../../components/buttons/selectButton/SelectButton.tsx";

const API_BASE = REST_API_URL;

export default function ClientStatsCard({ client, analytics, onClientUpdated }) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [labels, setLabels] = useState([]);
  const [labelOpen, setLabelOpen] = useState(false);
  const [currentLabel, setCurrentLabel] = useState(client.label || null);
  const labelDropdownRef = useRef(null);
 const userCurrency = useAppSelector(selectUserCurrency);
  useEffect(() => {
    axios
      .get(`${API_BASE}/client/client-labels/`, { headers: buildHeaders() })
      .then((res) => setLabels(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setCurrentLabel(client.label || null);
  }, [client.label]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(e.target)) {
        setLabelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const onSelectLabel = async (label) => {
    try {
      await axios.put(
        `${API_BASE}/client/client-view/${client.id}/`,
        { label: label.id },
        { headers: buildHeaders() }
      );
      setCurrentLabel(label);
      setLabelOpen(false);
      if (onClientUpdated) onClientUpdated();
    } catch (err) {
      console.error("Failed to assign label:", err);
    }
  };

  const avatar =
    client.image_url ||
    "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
        className="flex flex-col items-center justify-center bg-white rounded-[20px] p-5 gap-5 w-full h-[584px] shadow-sm relative"
      >
        <PrimaryButton
          variants={fadeUp}
          whileHover={{ scale: 1.05 }}
          onClick={() => setIsModalOpen(true)}
          label={<FiEdit2 className="w-5 h-5 " />}
          className="absolute top-[20px] right-[20px]"
          variant="outline"
        />

        <motion.div variants={fadeUp} className="rounded-full overflow-hidden bg-[#D9D9D9]">
          <motion.img
            variants={fadeUp}
            src={avatar}
            alt={client.name}
            className="w-full h-full object-cover"
          />
        </motion.div>

        <motion.div variants={fadeUp} className="flex flex-col items-center gap-2">
          <h3 className="font-normal text-[24px] leading-[28px] text-[#302B2B] text-center">
            {client.name || ""}
          </h3>

          {/* Label picker */}
          <div className="relative" ref={labelDropdownRef}>
            <PrimaryButton
              onClick={() => setLabelOpen((o) => !o)}
              variant="outline"
              size="small"
              label={
                <div className="flex items-center gap-1.5">
                  {currentLabel ? (
                    <>
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: currentLabel.color }}
                      />
                      <span style={{ color: currentLabel.color }} className="font-medium">
                        {currentLabel.name}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">{t("home.addLabelPlaceholder")}</span>
                  )}
                </div>
              }
              rightIcon={<FiChevronDown className="w-3.5 h-3.5 text-gray-400" />}
            />

            <AnimatePresence>
              {labelOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[160px] bg-white border border-gray-100 rounded-xl shadow-lg z-20 overflow-hidden"
                >
                  {labels.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-400 text-center">{t("home.stateNoLabels")}</div>
                  ) : (
                    labels.map((label) => (
                      <SelectButton
                        key={label.id}
                        selected={currentLabel?.id === label.id}
                        onClick={() => onSelectLabel(label)}
                        variant="check"
                        label={
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: label.color }}
                            />
                            <span className="text-sm font-medium" style={{ color: label.color }}>
                              {label.name}
                            </span>
                          </div>
                        }
                      />
                    ))
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="flex flex-col justify-between gap-[7px] w-[222px]">
          {[5, 4, 3, 2, 1].map((r, i) => {
            const value =
              ((analytics?.[`${r}_star_count`] || 0) / (analytics?.total_reviews || 1)) * 100;

            return (
              <motion.div
                key={r}
                variants={fadeUp}
                transition={{ delay: 0.2 * i }}
                className="flex flex-row items-center gap-[10px] w-[198px] h-[34px] mx-auto"
              >
                <div className="relative flex items-center justify-center w-[34px] h-[34px]">
                  <FaStar className="w-[34px] h-[34px] text-[#020637]" />
                  <span className="absolute text-white font-bold text-[14px] leading-[16px]">
                    {r}
                  </span>
                </div>
                <div className="w-[154px] h-[12px] bg-[#F6F6F6] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${value}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="h-full bg-[#030735] rounded-full"
                  />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex justify-between items-center bg-[#020637] text-white rounded-[10px] shadow-[0px_4px_6px_rgba(0,0,0,0.04)] px-[14px] py-[10px] gap-[13px] w-full h-[87px] mt-auto"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-[10px] w-[133px]">
            <div className="flex items-center justify-center w-[44px] h-[44px] bg-[#1e3aa0] rounded-full">
              <Tag className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[14px] text-white/90">{t("home.labelTotalIncome")}</span>
              <span className="text-[24px] font-bold text-white">
                {getCurrencySymbol(userCurrency)}{analytics?.total_price || 0}

              </span>
            </div>
          </motion.div>
          
        
          <div className="w-[2px] h-[67px] bg-[#1e3aa0]" />

          <motion.div variants={fadeUp} className="flex items-center gap-[10px] w-[140px]">
            <div className="flex items-center justify-center w-[44px] h-[44px] bg-[#1e3aa0] rounded-full">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[14px] text-white/90">{t("home.labelAppointments")}</span>
              <span className="text-[24px] font-bold text-white">
                {analytics?.appointment_count || 0}
              </span>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="w-[800px] max-w-[95vw]"
            >
              <AddClientModal
                onClose={() => setIsModalOpen(false)}
                client={client}
                isEditing={true}
                onUpdated={() => {
                  if (onClientUpdated) onClientUpdated();
                  setIsModalOpen(false);
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}